import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { AFF_API, getAffHeaders } from "../../context/AffAuthContext";
import { Bank, ArrowUpRight, Clock, CheckCircle, XCircle, PencilSimple, Plus } from "@phosphor-icons/react";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { toast } from "sonner";
import PanVerifyModal from "./PanVerifyModal";

function formatINR(num) {
  return `₹${Number(num).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function timeAgo(dateStr) {
  if (!dateStr) return "-";
  const diff = Date.now() - new Date(dateStr).getTime();
  const hrs = Math.floor(diff / 3600000);
  if (hrs < 1) return "< 1h ago";
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function statusBadge(status) {
  const styles = {
    pending: "bg-amber-500/10 text-amber-500 border-amber-500/20",
    approved: "bg-emerald-500/10 text-emerald-500 border-emerald-500/20",
    rejected: "bg-rose-500/10 text-rose-500 border-rose-500/20",
  };
  return <Badge variant="outline" className={`text-[10px] uppercase tracking-wider border ${styles[status] || ""}`}>{status}</Badge>;
}

function getErrorMessage(err) {
  const detail = err.response?.data?.detail;
  if (!detail) return "Failed";
  if (typeof detail === "object" && detail !== null) {
    return detail.message || JSON.stringify(detail);
  }
  return detail;
}

function BankDetailsCard({ bankDetails, onSave, isSelected, onSelect, wallet, onWithdrawDone, onRequestPanVerify }) {
  const [editing, setEditing] = useState(false);
  const [holder, setHolder] = useState(bankDetails?.account_holder_name || "");
  const [bank, setBank] = useState(bankDetails?.bank_name || "");
  const [accNum, setAccNum] = useState(bankDetails?.account_number || "");
  const [ifsc, setIfsc] = useState(bankDetails?.ifsc_code || "");
  const [saving, setSaving] = useState(false);

  // Withdrawal using saved bank
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const maxAmt = wallet?.direct_earnings || 0;

  useEffect(() => {
    if (bankDetails) {
      setHolder(bankDetails.account_holder_name || "");
      setBank(bankDetails.bank_name || "");
      setAccNum(bankDetails.account_number || "");
      setIfsc(bankDetails.ifsc_code || "");
    }
  }, [bankDetails]);

  const handleSave = async () => {
    if (!holder.trim() || !bank.trim() || !accNum.trim() || !ifsc.trim()) { toast.error("All fields required"); return; }
    setSaving(true);
    try {
      await axios.post(`${AFF_API}/affiliate/update-bank-details`, {
        account_holder_name: holder, bank_name: bank, account_number: accNum, ifsc_code: ifsc
      }, { headers: getAffHeaders() });
      toast.success("Bank details updated"); setEditing(false); onSave();
    } catch { toast.error("Update failed"); }
    finally { setSaving(false); }
  };

  const handleSavedWithdrawal = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0 || amt > maxAmt) { toast.error(`Enter valid amount (max ₹${maxAmt.toLocaleString("en-IN")})`); return; }
    setSubmitting(true);
    try {
      await axios.post(`${AFF_API}/affiliate/withdrawal`, {
        amount: amt,
        account_holder_name: bankDetails.account_holder_name,
        bank_name: bankDetails.bank_name,
        account_number: bankDetails.account_number,
        ifsc_code: bankDetails.ifsc_code
      }, { headers: getAffHeaders() });
      toast.success("Withdrawal submitted!");
      setAmount("");
      onWithdrawDone();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "PAN_REQUIRED_THRESHOLD") {
        toast.error(detail.message || "PAN verification required");
        onRequestPanVerify?.();
      } else {
        toast.error(getErrorMessage(err));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!editing && bankDetails) {
    return (
      <div
        className={`p-4 rounded-lg bg-zinc-950 border transition-all ${
          isSelected ? "border-emerald-500/60 bg-emerald-950/10 shadow-lg shadow-emerald-950/20" : "border-zinc-800"
        }`}
        data-testid="bank-details-display"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <p className="text-xs font-bold text-zinc-300">Saved Bank Details</p>
            {isSelected && (
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px]">
                Selected
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {!isSelected && (
              <button
                onClick={onSelect}
                data-testid="select-bank-btn"
                className="text-[10px] font-semibold px-2 py-0.5 rounded bg-emerald-600/20 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-600 hover:text-white transition-all"
              >
                Use for Withdrawal
              </button>
            )}
            <button
              onClick={() => setEditing(true)}
              data-testid="edit-bank-btn"
              className="text-[10px] text-blue-400 hover:text-blue-300 flex items-center gap-1"
            >
              <PencilSimple size={10} /> Edit
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs">
          <div><span className="text-zinc-600">Holder:</span> <span className="text-zinc-300">{bankDetails.account_holder_name}</span></div>
          <div><span className="text-zinc-600">Bank:</span> <span className="text-zinc-300">{bankDetails.bank_name}</span></div>
          <div><span className="text-zinc-600">Account:</span> <span className="text-zinc-300 font-mono">{bankDetails.account_number}</span></div>
          <div><span className="text-zinc-600">IFSC:</span> <span className="text-zinc-300 font-mono">{bankDetails.ifsc_code}</span></div>
        </div>

        {/* Withdrawal Form for Selected Saved Bank */}
        {isSelected && (
          <form onSubmit={handleSavedWithdrawal} className="mt-4 pt-3 border-t border-zinc-800/80 space-y-2.5" data-testid="saved-bank-withdrawal-form">
            <div className="flex items-center justify-between">
              <p className="text-xs font-semibold text-emerald-400">Withdraw to Saved Bank</p>
              <span className="text-[10px] text-zinc-400">Max: {formatINR(maxAmt)}</span>
            </div>
            <Input
              data-testid="saved-bank-withdraw-amount"
              type="number"
              placeholder="₹ Amount"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="bg-zinc-900 border-zinc-800 text-zinc-100 text-lg font-mono"
            />
            <button
              type="submit"
              disabled={submitting}
              data-testid="saved-bank-submit-btn"
              className="w-full py-2.5 rounded-md text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 transition-all"
            >
              {submitting ? "Processing..." : "Submit Withdrawal"}
            </button>
          </form>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 rounded-lg bg-zinc-950 border border-zinc-800 space-y-2" data-testid="bank-details-form">
      <p className="text-xs font-bold text-zinc-300 mb-2">{bankDetails ? "Update Bank Details" : "Add Bank Details"}</p>
      <Input data-testid="edit-bank-holder" placeholder="Account Holder Name" value={holder} onChange={(e) => setHolder(e.target.value)} className="bg-zinc-900 border-zinc-800 text-zinc-100 text-sm" />
      <Input data-testid="edit-bank-name" placeholder="Bank Name" value={bank} onChange={(e) => setBank(e.target.value)} className="bg-zinc-900 border-zinc-800 text-zinc-100 text-sm" />
      <Input data-testid="edit-bank-account" placeholder="Account Number" value={accNum} onChange={(e) => setAccNum(e.target.value)} className="bg-zinc-900 border-zinc-800 text-zinc-100 text-sm" />
      <Input data-testid="edit-bank-ifsc" placeholder="IFSC Code" value={ifsc} onChange={(e) => setIfsc(e.target.value)} className="bg-zinc-900 border-zinc-800 text-zinc-100 text-sm" />
      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving} data-testid="save-bank-btn"
          className="flex-1 py-2 rounded-md text-xs font-bold bg-blue-600 text-white hover:bg-blue-500 disabled:opacity-50 transition-all">
          {saving ? "Saving..." : "Save"}
        </button>
        {bankDetails && <button onClick={() => setEditing(false)} className="px-4 py-2 rounded-md text-xs border border-zinc-800 text-zinc-400 hover:text-zinc-100 transition-all">Cancel</button>}
      </div>
    </div>
  );
}

function WithdrawForm({ wallet, onDone, onRequestPanVerify }) {
  const [amount, setAmount] = useState("");
  const [holder, setHolder] = useState("");
  const [bank, setBank] = useState("");
  const [accNum, setAccNum] = useState("");
  const [ifsc, setIfsc] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const maxAmt = wallet?.direct_earnings || 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0 || amt > maxAmt) { toast.error(`Enter valid amount (max ₹${maxAmt.toLocaleString("en-IN")})`); return; }
    if (!holder.trim() || !bank.trim() || !accNum.trim() || !ifsc.trim()) { toast.error("All bank details required"); return; }
    setSubmitting(true);
    try {
      await axios.post(`${AFF_API}/affiliate/withdrawal`, {
        amount: amt, account_holder_name: holder, bank_name: bank, account_number: accNum, ifsc_code: ifsc
      }, { headers: getAffHeaders() });
      toast.success("Withdrawal submitted!"); setAmount(""); onDone();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "PAN_REQUIRED_THRESHOLD") {
        toast.error(detail.message || "PAN verification required");
        onRequestPanVerify?.();
      } else {
        toast.error(getErrorMessage(err));
      }
    }
    finally { setSubmitting(false); }
  };

  return (
    <form onSubmit={handleSubmit} className="p-4 rounded-lg bg-zinc-950 border border-zinc-800 space-y-2" data-testid="new-withdrawal-form">
      <p className="text-xs font-bold text-zinc-300">New Withdrawal (Max: {formatINR(maxAmt)})</p>
      <Input data-testid="txn-withdraw-amount" type="number" placeholder="₹ Amount" value={amount} onChange={(e) => setAmount(e.target.value)} className="bg-zinc-900 border-zinc-800 text-zinc-100 text-lg font-mono" />
      <Input data-testid="txn-holder" placeholder="Account Holder Name" value={holder} onChange={(e) => setHolder(e.target.value)} className="bg-zinc-900 border-zinc-800 text-zinc-100 text-sm" />
      <Input data-testid="txn-bank" placeholder="Bank Name" value={bank} onChange={(e) => setBank(e.target.value)} className="bg-zinc-900 border-zinc-800 text-zinc-100 text-sm" />
      <Input data-testid="txn-account" placeholder="Account Number" value={accNum} onChange={(e) => setAccNum(e.target.value)} className="bg-zinc-900 border-zinc-800 text-zinc-100 text-sm" />
      <Input data-testid="txn-ifsc" placeholder="IFSC Code" value={ifsc} onChange={(e) => setIfsc(e.target.value)} className="bg-zinc-900 border-zinc-800 text-zinc-100 text-sm" />
      <button type="submit" disabled={submitting} data-testid="txn-submit-btn"
        className="w-full py-2.5 rounded-md text-xs font-bold bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-50 transition-all">
        {submitting ? "Processing..." : "Submit Withdrawal"}
      </button>
    </form>
  );
}

export default function TransactionsPage({ wallet, onUpdate }) {
  const [data, setData] = useState({ withdrawals: [], bank_details: null });
  const [loading, setLoading] = useState(true);
  const [selectedBankForWithdraw, setSelectedBankForWithdraw] = useState(false);
  const [panModalOpen, setPanModalOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${AFF_API}/affiliate/transactions`, { headers: getAffHeaders() });
      setData(res.data);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div className="space-y-6" data-testid="transactions-page">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: Withdraw + Bank Details */}
        <div className="space-y-4">
          {selectedBankForWithdraw && data.bank_details ? (
            <div className="p-4 rounded-lg bg-zinc-950 border border-zinc-800 flex items-center justify-between" data-testid="new-withdrawal-button-card">
              <div>
                <p className="text-xs font-bold text-zinc-300">Custom Withdrawal</p>
                <p className="text-[11px] text-zinc-500">Withdraw to a different account</p>
              </div>
              <button
                onClick={() => setSelectedBankForWithdraw(false)}
                data-testid="show-new-withdrawal-btn"
                className="px-3 py-1.5 rounded-md text-xs font-bold bg-blue-600 text-white hover:bg-blue-500 transition-all flex items-center gap-1 shadow-sm"
              >
                <Plus size={14} weight="bold" /> New Withdrawal
              </button>
            </div>
          ) : (
            <WithdrawForm wallet={wallet} onDone={fetchData} onRequestPanVerify={() => setPanModalOpen(true)} />
          )}

          <BankDetailsCard
            bankDetails={data.bank_details}
            onSave={fetchData}
            isSelected={selectedBankForWithdraw && !!data.bank_details}
            onSelect={() => setSelectedBankForWithdraw(true)}
            wallet={wallet}
            onWithdrawDone={fetchData}
            onRequestPanVerify={() => setPanModalOpen(true)}
          />
        </div>

        {/* Right: Withdrawal History */}
        <div className="lg:col-span-2 rounded-xl border border-zinc-800/50 bg-zinc-900/80 backdrop-blur-md p-5" data-testid="withdrawal-history">
          <div className="flex items-center gap-2 mb-4">
            <Bank size={18} weight="duotone" className="text-emerald-500" />
            <h3 className="text-sm font-bold text-zinc-50" style={{ fontFamily: "Manrope" }}>Withdrawal History</h3>
          </div>

          {loading ? (
            <div className="h-32 flex items-center justify-center">
              <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : data.withdrawals.length === 0 ? (
            <div className="py-10 text-center">
              <Bank size={28} className="text-zinc-700 mx-auto mb-2" />
              <p className="text-xs text-zinc-500">No withdrawals yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {data.withdrawals.map((w) => (
                <div key={w.withdrawal_id} className="flex items-center justify-between p-3 rounded-lg bg-zinc-950 border border-zinc-800/50 hover:border-zinc-700 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                      w.status === "approved" ? "bg-emerald-500/10" : w.status === "rejected" ? "bg-rose-500/10" : "bg-amber-500/10"
                    }`}>
                      {w.status === "approved" ? <CheckCircle size={16} className="text-emerald-500" /> :
                       w.status === "rejected" ? <XCircle size={16} className="text-rose-500" /> :
                       <Clock size={16} className="text-amber-500" />}
                    </div>
                    <div>
                      <p className="text-sm font-bold text-zinc-100" style={{ fontFamily: "JetBrains Mono, monospace" }}>{formatINR(w.amount)}</p>
                      <p className="text-[10px] text-zinc-500">{timeAgo(w.requested_at)} &middot; {w.bank_details?.bank_name || "-"}</p>
                    </div>
                  </div>
                  {statusBadge(w.status)}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <PanVerifyModal
        open={panModalOpen}
        onClose={() => setPanModalOpen(false)}
        initialPan=""
        onVerified={() => {
          setPanModalOpen(false);
          fetchData();
          onUpdate?.();
        }}
      />
    </div>
  );
}
